import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  CircularProgress,
  Switch,
  Divider,
} from '@mui/material';
import { Save, Description, Refresh } from '@mui/icons-material';
import { Percent, DollarSign } from 'lucide-react';
import { getBillingSettings, saveBillingSettings } from '../firebase/settingsService';

const BillingSettings = () => {
  const [settings, setSettings] = useState({
    companyName: 'Roshan Gram Store',
    companyAddress: '123 Business Street, City, State 12345',
    companyPhone: '+1 (234) 567-8900',
    companyEmail: 'info@roshangramstore.com',
    taxRate: '10',
    currency: 'LKR',
    invoicePrefix: 'INV',
    companyLogo: '', // Logo URL
    includeCompanyLogo: true,
    includeTermsAndConditions: true,
    includePaymentInstructions: true,
    includeTaxBreakdown: true,
    includeDiscountField: true,
    includeNotes: true,
    termsText: 'Payment is due within 30 days. Late payments may incur additional charges.',
    paymentInstructions: 'Please make payment via bank transfer or credit card.',
    globalDiscount: {
      enabled: false,
      type: 'percentage', // 'percentage' or 'fixed'
      value: 0,
      reason: '',
    },
    loyaltyDiscount: {
      enabled: true,
      discountType: 'percentage',
      discountValue: 5,
      minOrdersRequired: 1,
      message: 'Loyalty Discount Applied! Thank you for being a valued customer.'
    },
    kokoChargePercentage: 12, // Default 12% Koko service charge
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    const result = await getBillingSettings();
    if (result.success) {
      setSettings(result.data);
    } else {
      showSnackbar(result.error || 'Failed to load settings', 'error');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadSettings();
    setRefreshing(false);
    showSnackbar('Settings refreshed');
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleGlobalDiscountChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      globalDiscount: {
        ...prev.globalDiscount,
        [field]: value
      }
    }));
  };

  const handleLoyaltyDiscountChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      loyaltyDiscount: {
        ...prev.loyaltyDiscount,
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    const result = await saveBillingSettings(settings);
    if (result.success) {
      showSnackbar('Settings saved successfully!');
    } else {
      showSnackbar(result.error || 'Failed to save settings', 'error');
    }
    setSaving(false);
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) { // 2MB limit
        showSnackbar('Logo file size should be less than 2MB', 'error');
        return;
      }
      
      setUploadingLogo(true);
      const reader = new FileReader();
      reader.onloadend = () => {
        setSettings({ ...settings, companyLogo: reader.result });
        setUploadingLogo(false);
        showSnackbar('Logo uploaded successfully');
      };
      reader.onerror = () => {
        setUploadingLogo(false);
        showSnackbar('Failed to upload logo', 'error');
      };
      reader.readAsDataURL(file);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Billing Settings
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Customize your invoice templates and billing preferences
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={refreshing ? <CircularProgress size={20} /> : <Refresh />}
            onClick={handleRefresh}
            disabled={refreshing}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Company Information
              </Typography>
              
              {/* Logo Upload */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Company Logo
                </Typography>
                {settings.companyLogo && (
                  <Box sx={{ mb: 2, textAlign: 'center' }}>
                    <img 
                      src={settings.companyLogo} 
                      alt="Company Logo" 
                      style={{ maxWidth: '200px', maxHeight: '100px', objectFit: 'contain' }}
                    />
                  </Box>
                )}
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  disabled={uploadingLogo}
                >
                  {uploadingLogo ? 'Uploading...' : settings.companyLogo ? 'Change Logo' : 'Upload Logo'}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleLogoUpload}
                  />
                </Button>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Recommended: PNG or JPG, max 2MB
                </Typography>
              </Box>

              <TextField
                fullWidth
                label="Company Name"
                name="companyName"
                value={settings.companyName}
                onChange={handleChange}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Company Address"
                name="companyAddress"
                value={settings.companyAddress}
                onChange={handleChange}
                multiline
                rows={3}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Phone Number"
                name="companyPhone"
                value={settings.companyPhone}
                onChange={handleChange}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Email Address"
                name="companyEmail"
                type="email"
                value={settings.companyEmail}
                onChange={handleChange}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Invoice Settings
              </Typography>
              <TextField
                fullWidth
                label="Tax Rate (%)"
                name="taxRate"
                type="number"
                value={settings.taxRate}
                onChange={handleChange}
                sx={{ mb: 2 }}
              />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Currency</InputLabel>
                <Select
                  name="currency"
                  value={settings.currency}
                  label="Currency"
                  onChange={handleChange}
                >
                  <MenuItem value="LKR">LKR - Sri Lankan Rupee</MenuItem>
                  <MenuItem value="USD">USD - US Dollar</MenuItem>
                  <MenuItem value="EUR">EUR - Euro</MenuItem>
                  <MenuItem value="GBP">GBP - British Pound</MenuItem>
                  <MenuItem value="INR">INR - Indian Rupee</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Invoice Prefix"
                name="invoicePrefix"
                value={settings.invoicePrefix}
                onChange={handleChange}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Description sx={{ mr: 1 }} color="action" />
            <Typography variant="h6" fontWeight={600}>
              Invoice Components
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="includeCompanyLogo"
                    checked={settings.includeCompanyLogo}
                    onChange={handleChange}
                  />
                }
                label="Include Company Logo"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="includeTaxBreakdown"
                    checked={settings.includeTaxBreakdown}
                    onChange={handleChange}
                  />
                }
                label="Include Tax Breakdown"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="includeDiscountField"
                    checked={settings.includeDiscountField}
                    onChange={handleChange}
                  />
                }
                label="Include Discount Field"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="includeNotes"
                    checked={settings.includeNotes}
                    onChange={handleChange}
                  />
                }
                label="Include Notes Section"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="includeTermsAndConditions"
                    checked={settings.includeTermsAndConditions}
                    onChange={handleChange}
                  />
                }
                label="Include Terms & Conditions"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="includePaymentInstructions"
                    checked={settings.includePaymentInstructions}
                    onChange={handleChange}
                  />
                }
                label="Include Payment Instructions"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {settings.includeTermsAndConditions && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Terms and Conditions
            </Typography>
            <TextField
              fullWidth
              name="termsText"
              value={settings.termsText}
              onChange={handleChange}
              multiline
              rows={4}
            />
          </CardContent>
        </Card>
      )}

      {settings.includePaymentInstructions && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Payment Instructions
            </Typography>
            <TextField
              fullWidth
              name="paymentInstructions"
              value={settings.paymentInstructions}
              onChange={handleChange}
              multiline
              rows={3}
            />
          </CardContent>
        </Card>
      )}

      <Card sx={{ mb: 3, border: '2px solid', borderColor: 'primary.main' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Percent size={24} color="primary" />
              <Typography variant="h6" fontWeight={600}>
                🎯 Global Discount (All Products)
              </Typography>
            </Box>
            <Switch
              checked={settings.globalDiscount?.enabled || false}
              onChange={(e) => handleGlobalDiscountChange('enabled', e.target.checked)}
              size="medium"
            />
          </Box>

          <Divider sx={{ mb: 2 }} />

          {settings.globalDiscount?.enabled && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                This discount will be applied to all products in billing. Current discount: 
                <strong>
                  {(settings.globalDiscount?.type === 'percentage') 
                    ? ` ${settings.globalDiscount?.value}%` 
                    : ` ${settings.currency} ${settings.globalDiscount?.value}`}
                </strong>
              </Alert>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Discount Type</InputLabel>
                    <Select
                      value={settings.globalDiscount?.type || 'percentage'}
                      label="Discount Type"
                      onChange={(e) => handleGlobalDiscountChange('type', e.target.value)}
                    >
                      <MenuItem value="percentage">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Percent size={18} /> Percentage (%)
                        </Box>
                      </MenuItem>
                      <MenuItem value="fixed">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <DollarSign size={18} /> Fixed Amount ({settings.currency})
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={(settings.globalDiscount?.type === 'percentage') ? 'Discount %' : `Discount (${settings.currency})`}
                    type="number"
                    inputProps={{ 
                      step: (settings.globalDiscount?.type === 'percentage') ? '0.1' : '1',
                      min: '0',
                      max: (settings.globalDiscount?.type === 'percentage') ? '100' : undefined
                    }}
                    value={settings.globalDiscount?.value || 0}
                    onChange={(e) => handleGlobalDiscountChange('value', parseFloat(e.target.value) || 0)}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Discount Reason (Optional)"
                    placeholder="e.g., Seasonal Sale, Bulk Order, Loyalty Program"
                    value={settings.globalDiscount?.reason || ''}
                    onChange={(e) => handleGlobalDiscountChange('reason', e.target.value)}
                    helperText="This helps track why the discount was applied"
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  💡 <strong>Example:</strong> If you set 10% discount, every product price will be reduced by 10% in billing.
                </Typography>
              </Box>
            </Box>
          )}

          {!settings.globalDiscount?.enabled && (
            <Typography variant="body2" color="text.secondary">
              Enable the toggle above to apply a discount to all products.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Loyalty Discount Settings */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" fontWeight={600}>
                🎉 Loyalty Discount (Returning Customers)
              </Typography>
            </Box>
            <Switch
              checked={settings.loyaltyDiscount?.enabled !== false}
              onChange={(e) => handleLoyaltyDiscountChange('enabled', e.target.checked)}
              size="medium"
            />
          </Box>

          <Divider sx={{ mb: 2 }} />

          {settings.loyaltyDiscount?.enabled !== false && (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                Loyalty discount will automatically appear for customers who have made{' '}
                <strong>{settings.loyaltyDiscount?.minOrdersRequired || 1}</strong> or more previous purchases.
                Current discount: 
                <strong>
                  {(settings.loyaltyDiscount?.discountType === 'percentage') 
                    ? ` ${settings.loyaltyDiscount?.discountValue || 5}%` 
                    : ` ${settings.currency} ${settings.loyaltyDiscount?.discountValue || 0}`}
                </strong>
              </Alert>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Discount Type</InputLabel>
                    <Select
                      value={settings.loyaltyDiscount?.discountType || 'percentage'}
                      label="Discount Type"
                      onChange={(e) => handleLoyaltyDiscountChange('discountType', e.target.value)}
                    >
                      <MenuItem value="percentage">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Percent size={18} /> Percentage (%)
                        </Box>
                      </MenuItem>
                      <MenuItem value="fixed">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <DollarSign size={18} /> Fixed Amount ({settings.currency})
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={(settings.loyaltyDiscount?.discountType === 'percentage') ? 'Discount %' : `Discount (${settings.currency})`}
                    type="number"
                    inputProps={{ 
                      step: (settings.loyaltyDiscount?.discountType === 'percentage') ? '0.1' : '1',
                      min: '0',
                      max: (settings.loyaltyDiscount?.discountType === 'percentage') ? '100' : undefined
                    }}
                    value={settings.loyaltyDiscount?.discountValue || 5}
                    onChange={(e) => handleLoyaltyDiscountChange('discountValue', parseFloat(e.target.value) || 0)}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Minimum Orders Required"
                    type="number"
                    inputProps={{ min: '1', step: '1' }}
                    value={settings.loyaltyDiscount?.minOrdersRequired || 1}
                    onChange={(e) => handleLoyaltyDiscountChange('minOrdersRequired', parseInt(e.target.value) || 1)}
                    helperText="Number of previous orders needed to qualify"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 2, bgcolor: 'success.lighter', borderRadius: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                      Loyalty Tiers (Automatic)
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                      🥉 Bronze: 1-9 orders (Base discount)<br/>
                      🥈 Silver: 10-19 orders (+1% bonus)<br/>
                      🥇 Gold: 20-49 orders (+2% bonus)<br/>
                      💎 Platinum: 50+ orders (+3% bonus)
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Loyalty Message"
                    placeholder="Thank you for being a valued customer!"
                    value={settings.loyaltyDiscount?.message || 'Loyalty Discount Applied! Thank you for being a valued customer.'}
                    onChange={(e) => handleLoyaltyDiscountChange('message', e.target.value)}
                    helperText="This message will be shown when loyalty discount is applied"
                    multiline
                    rows={2}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  💡 <strong>How it works:</strong> When a registered customer with previous purchases is selected in billing, 
                  a loyalty discount alert will automatically appear. The cashier can then apply the discount with one click.
                  Higher tier customers automatically get bonus discounts!
                </Typography>
              </Box>
            </Box>
          )}

          {settings.loyaltyDiscount?.enabled === false && (
            <Typography variant="body2" color="text.secondary">
              Enable the toggle above to reward returning customers with automatic loyalty discounts.
            </Typography>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mb: 3, border: '2px solid', borderColor: 'warning.main' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Percent size={24} color="orange" />
            <Typography variant="h6" fontWeight={600}>
              💳 Koko Payment Service Charge
            </Typography>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Alert severity="info" sx={{ mb: 2 }}>
            This percentage will be added to the total bill amount for all Koko payment type purchases. 
            Current charge: <strong>{settings.kokoChargePercentage || 12}%</strong>
          </Alert>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Koko Service Charge %"
                type="number"
                inputProps={{ 
                  step: '0.1',
                  min: '0',
                  max: '100'
                }}
                value={settings.kokoChargePercentage || 12}
                onChange={(e) => setSettings({ ...settings, kokoChargePercentage: parseFloat(e.target.value) || 12 })}
                helperText="Percentage added to total for Koko payments (default: 12%)"
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              💡 <strong>Example:</strong> If bill total is LKR 10,000 and Koko charge is 12%, 
              the customer will pay LKR 11,200 (10,000 + 1,200 service charge).
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>🖨️ Thermal Printer</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Configure your thermal receipt printer. Leave blank to use browser PDF print as fallback.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Printer Name / IP Address"
                value={settings.thermalPrinterName || ''}
                onChange={e => setSettings({ ...settings, thermalPrinterName: e.target.value })}
                placeholder="e.g. POS-80 or 192.168.1.100"
                helperText="Name or IP of your thermal printer on the network" />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Paper Width</InputLabel>
                <Select value={settings.thermalPaperWidth || '80mm'} label="Paper Width"
                  onChange={e => setSettings({ ...settings, thermalPaperWidth: e.target.value })}>
                  <MenuItem value="58mm">58mm</MenuItem>
                  <MenuItem value="80mm">80mm</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Print Mode</InputLabel>
                <Select value={settings.thermalPrintMode || 'browser'} label="Print Mode"
                  onChange={e => setSettings({ ...settings, thermalPrintMode: e.target.value })}>
                  <MenuItem value="browser">Browser Print (PDF)</MenuItem>
                  <MenuItem value="thermal">Thermal Direct</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={!!settings.thermalAutoPrint}
                  onChange={e => setSettings({ ...settings, thermalAutoPrint: e.target.checked })} />}
                label="Auto-print receipt after each sale"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Button
        variant="contained"
        size="large"
        startIcon={saving ? <CircularProgress size={20} /> : <Save />}
        onClick={handleSave}
        disabled={saving}
      >
        {saving ? 'Saving...' : 'Save Settings'}
      </Button>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BillingSettings;
